import React, { useMemo, useState } from 'react';
import { isStandaloneMode, isUnseededStandalone } from '../../utils/env';
import scenarioCatalog from '../../utils/dev-scenario-catalog';

const { SCENARIOS, DEFAULT_SCENARIO, FORCE_UPGRADE_SCENARIO, isSupportedScenario } =
    scenarioCatalog;

const styles = {
    toolbar: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 30,
        paddingBottom: 'env(safe-area-inset-bottom)',
    },
    surface: {
        borderTop: '1px solid rgba(197, 216, 204, 0.95)',
        background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(246, 251, 247, 0.98) 100%)',
        boxShadow: '0 -8px 24px rgba(20, 32, 20, 0.10), 0 -2px 6px rgba(20, 32, 20, 0.06)',
        backdropFilter: 'blur(18px)',
    },
    bar: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        minHeight: '44px',
        padding: '6px 20px',
        flexWrap: 'wrap',
    },
    eyebrow: {
        margin: 0,
        color: '#5f6f66',
        fontSize: '1rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    status: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    subtleLabel: {
        color: '#5f6f66',
        fontSize: '1.2rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: '24px',
        padding: '0 10px',
        borderRadius: '999px',
        background: 'linear-gradient(135deg, #2fcf4d 0%, #18a54c 100%)',
        color: '#fff',
        fontSize: '1.2rem',
        fontWeight: 700,
        lineHeight: 1,
    },
    spacer: {
        flex: '1 1 auto',
    },
    inlineForm: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: 0,
    },
    select: {
        boxSizing: 'border-box',
        height: '28px',
        minHeight: '28px',
        padding: '0 24px 0 10px',
        border: '1px solid rgba(197, 216, 204, 0.95)',
        borderRadius: '6px',
        background: '#fff',
        color: '#1d2521',
        fontSize: '1.2rem',
        lineHeight: 1,
        appearance: 'none',
        WebkitAppearance: 'none',
    },
    submitButton: {
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        height: '28px',
        padding: '0 12px',
        border: '1px solid #000',
        borderRadius: '6px',
        background: '#000',
        color: '#fff',
        fontSize: '1.2rem',
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        flex: '0 0 auto',
    },
    toggleButton: {
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        height: '28px',
        padding: '0 12px',
        border: '1px solid rgba(197, 216, 204, 0.95)',
        borderRadius: '6px',
        background: '#fff',
        color: '#1d2521',
        fontSize: '1.2rem',
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        flex: '0 0 auto',
    },
    error: {
        padding: '6px 20px',
        borderBottom: '1px solid rgba(197, 216, 204, 0.8)',
    },
};

/**
 * Builds the next standalone URL after seeding a scenario user.
 *
 * @param {object} options
 * @param {string} options.currentHref
 * @param {string} options.email
 * @param {string} options.scenario
 * @returns {string}
 */
function buildSeededStandaloneUrl({ currentHref, email, scenario }) {
    const nextUrl = new URL(currentHref);
    nextUrl.searchParams.set('test-email', email);
    nextUrl.searchParams.set('scenario', scenario);

    if (scenario === FORCE_UPGRADE_SCENARIO) {
        nextUrl.searchParams.set('force-upgrade', 'true');
    } else {
        nextUrl.searchParams.delete('force-upgrade');
    }

    return nextUrl.toString();
}

/**
 * Resolves the currently active standalone scenario from the URL.
 *
 * @param {string} currentHref
 * @returns {string}
 */
function getActiveScenarioIdFromHref(currentHref) {
    try {
        const currentUrl = new URL(currentHref);
        const queryScenario = currentUrl.searchParams.get('scenario');
        const forceUpgrade = currentUrl.searchParams.get('force-upgrade');

        if (isSupportedScenario(queryScenario)) {
            return queryScenario;
        }

        if (forceUpgrade === 'true' || forceUpgrade === '1') {
            return FORCE_UPGRADE_SCENARIO;
        }
    } catch (error) {
        return DEFAULT_SCENARIO;
    }

    return DEFAULT_SCENARIO;
}

/**
 * Seeds a Daisy scenario user through the local Netlify adapter endpoint.
 *
 * @param {string} scenario
 * @returns {Promise<{email: string, userId: number|string, scenario: string}>}
 */
async function createScenarioUser(scenario) {
    const response = await fetch('/api/dev-create-scenario-user', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scenario }),
    });

    const payload = await response.json();
    if (!response.ok) {
        throw new Error(payload?.error?.message || 'Unable to seed scenario user.');
    }

    return payload;
}

/**
 * Dev-only toolbar rendered at the viewport bottom in standalone mode
 * (`:8889`). Lets developers seed a Daisy scenario user on demand and swap
 * the active scenario. Has two visual states:
 *
 * - **Unseeded** (no `test-email` in the URL): inline scenario picker and
 *   submit button are always visible; there is no Active badge or toggle.
 * - **Seeded**: collapsed to an Active badge and a Change toggle; expanding
 *   reveals the same inline picker so the developer can seed a new user.
 *
 * Returns `null` in iframe mode so BMA embedded inside Ghost Admin is
 * unaffected.
 *
 * @returns {JSX.Element|null}
 */
function StandaloneScenarioSeedPanel() {
    const unseeded = useMemo(() => isUnseededStandalone(), []);
    const activeScenarioId = useMemo(() => {
        return getActiveScenarioIdFromHref(window.location.href);
    }, []);
    const [scenario, setScenario] = useState(() => activeScenarioId);
    const [error, setError] = useState('');
    const [isExpanded, setIsExpanded] = useState(unseeded);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedScenario = useMemo(() => {
        return SCENARIOS.find((entry) => entry.id === scenario) || SCENARIOS[0];
    }, [scenario]);

    const activeScenario = useMemo(() => {
        return SCENARIOS.find((entry) => entry.id === activeScenarioId) || SCENARIOS[0];
    }, [activeScenarioId]);

    if (!isStandaloneMode()) {
        return null;
    }

    /**
     * Seeds the selected scenario via the local `/api/dev-create-scenario-user`
     * adapter. On success, rewrites the URL with the seeded `test-email` +
     * `scenario` and hard-reloads so the app boots against the new Daisy user.
     * On failure, surfaces the error message inline without navigating.
     *
     * @param {Event} event
     * @returns {Promise<void>}
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const seededUser = await createScenarioUser(scenario);
            const nextUrl = buildSeededStandaloneUrl({
                currentHref: window.location.href,
                email: seededUser.email,
                scenario: seededUser.scenario,
            });

            window.location.assign(nextUrl);
        } catch (submitError) {
            setError(submitError.message);
            setIsSubmitting(false);
        }
    };

    const showInlineForm = unseeded || isExpanded;

    return (
        <section className="standalone-seed-toolbar" data-test-id="standalone-seed-panel">
            <div style={styles.toolbar}>
                <div style={styles.surface}>
                    {error ? (
                        <div
                            className="form-error standalone-seed-toolbar__error"
                            style={styles.error}
                        >
                            <p className="error-msg" data-test-id="standalone-seed-error">
                                {error}
                            </p>
                        </div>
                    ) : null}

                    <div style={styles.bar}>
                        <span style={styles.eyebrow}>Standalone seeding</span>
                        {unseeded ? (
                            <div style={styles.status} role="status" aria-live="polite">
                                <span
                                    style={styles.subtleLabel}
                                    data-test-id="standalone-seed-unseeded"
                                >
                                    No user seeded yet
                                </span>
                            </div>
                        ) : (
                            <div style={styles.status} role="status" aria-live="polite">
                                <span style={styles.subtleLabel}>Active</span>
                                <span
                                    style={styles.badge}
                                    data-test-id="standalone-seed-active-scenario"
                                >
                                    {activeScenario.label}
                                </span>
                            </div>
                        )}
                        <div style={styles.spacer} />
                        {showInlineForm ? (
                            <form style={styles.inlineForm} onSubmit={handleSubmit}>
                                <label
                                    htmlFor="standalone-seed-scenario"
                                    style={styles.subtleLabel}
                                >
                                    Seed
                                </label>
                                <select
                                    id="standalone-seed-scenario"
                                    className="standalone-seed-toolbar__select"
                                    style={styles.select}
                                    data-test-id="standalone-seed-scenario-select"
                                    value={scenario}
                                    onChange={(event) => setScenario(event.target.value)}
                                    disabled={isSubmitting}
                                    aria-label={`Seed scenario — ${selectedScenario.description}`}
                                >
                                    {SCENARIOS.map((entry) => {
                                        return (
                                            <option key={entry.id} value={entry.id}>
                                                {entry.label}
                                            </option>
                                        );
                                    })}
                                </select>
                                <button
                                    type="submit"
                                    className="standalone-seed-toolbar__submit"
                                    style={styles.submitButton}
                                    disabled={isSubmitting}
                                    data-test-id="standalone-seed-submit"
                                >
                                    {isSubmitting ? 'Seeding…' : 'Seed and reload'}
                                </button>
                            </form>
                        ) : null}
                        {unseeded ? null : (
                            <button
                                type="button"
                                className="standalone-seed-toolbar__toggle"
                                style={styles.toggleButton}
                                onClick={() => setIsExpanded((isOpen) => !isOpen)}
                                aria-expanded={isExpanded}
                                data-test-id="standalone-seed-toggle"
                            >
                                {isExpanded ? 'Hide' : 'Change'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default StandaloneScenarioSeedPanel;

export const _private = {
    getActiveScenarioIdFromHref,
    buildSeededStandaloneUrl,
    createScenarioUser,
};
